import React, { MouseEventHandler, useEffect, useState } from "react";
import { Spinner, Table, } from "react-bootstrap";
import {
    flexRender,
    getCoreRowModel,
    useReactTable,
    ColumnDef,
    getPaginationRowModel,
    getSortedRowModel,
    getExpandedRowModel,
    Row,
    SortingState,
    ExpandedState,
} from "@tanstack/react-table";

import { SortAscendingIcon } from "Components/Icons/SortAscending";
import { SortDescendingIcon } from "Components/Icons/SortDescending";
import { Defaults } from "@/common/utils";


interface StyledTableProps {
    defaultData: any[],
    columns: ColumnDef<any>[],
    getRowCanExpand?: (row: Row<any>) => boolean,
    onIsExpanded?: (row: Row<any>) => React.ReactNode,
    expanded?: ExpandedState,
    setExpanded?: (e: ExpandedState) => void,
    className: string,
    onClickRow?: (row: Row<any>) => MouseEventHandler<HTMLTableRowElement> | undefined,
    noDataMessage?: string,
    totalPagesNumber?: number, // for tables with API data pagination, total number of pages
    currentPageNumber?: number, // for tables with API data pagination, current page number
    setPageSize?: (n: number) => void, // for tables with API data pagination, to get the page size set in this component
    setCurrentNumber?: (n: number) => void, // for tables with API data pagination, to get the new page number set in this component
    setSorting?: (e: any) => void, // for tables with API data pagination, to get the sorting set in this component
    sorting?: SortingState, // receives sorting values
    pageSize?: number, // for tables with API data pagination, current page size (controlled by parent)
    initialPageSize?: number, // initial page size (for client-side pagination)
    showRowsPerPage?: boolean, // Show rows per page option
    autoResetPageIndex?: boolean, // reset page index when data changes
    isLoading?: boolean, // loading state
    manualSorting?: boolean
}

export const StyledTable = (
    {
        defaultData,
        columns,
        getRowCanExpand,
        onIsExpanded,
        expanded,
        setExpanded,
        className,
        onClickRow,
        noDataMessage,
        totalPagesNumber,
        currentPageNumber,
        setPageSize,
        setCurrentNumber,
        setSorting,
        sorting,
        manualSorting,
        pageSize: pageSizeProp,
        initialPageSize = Defaults.tablePageSize(),
        showRowsPerPage = true,
        autoResetPageIndex = true,
        isLoading = false,
    }: StyledTableProps
) => {
    const [data, setData] = useState(() => [...defaultData]);
    const [localSorting, setLocalSorting] = useState([]);
    const [clientPageIndex, setClientPageIndex] = useState(0);
    const effectivePageSize = pageSizeProp ?? initialPageSize;
    const paginationState = totalPagesNumber
        ? { pageIndex: currentPageNumber || 0, pageSize: effectivePageSize }
        : (pageSizeProp !== undefined
            ? { pageIndex: clientPageIndex, pageSize: effectivePageSize }
            : undefined);
    const table = useReactTable<any>({
        data,
        columns,
        getRowCanExpand,
        getExpandedRowModel: getExpandedRowModel(),
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        state: {
            sorting: sorting ?? localSorting,
            expanded,
            ...(paginationState && { pagination: paginationState }),
        },
        onPaginationChange: pageSizeProp !== undefined && !totalPagesNumber
            ? (updater) => {
                const prev = { pageIndex: clientPageIndex, pageSize: effectivePageSize };
                const newState = typeof updater === "function" ? updater(prev) : updater;
                setClientPageIndex(newState.pageIndex);
                setPageSize?.(newState.pageSize);
            }
            : undefined,
        onExpandedChange: setExpanded,
        onSortingChange: setSorting ?? setLocalSorting,
        autoResetPageIndex: autoResetPageIndex,
        initialState: {
            pagination: {
                pageSize: initialPageSize,
            },
        },
        manualSorting,
        manualPagination: !!totalPagesNumber,
    });
    const totalPages = totalPagesNumber ?? table.getPageCount();
    const currentPage = currentPageNumber ?? table.getState().pagination.pageIndex;

    const firstValue = () => {
        if (currentPage < 3) { return 1; }
        else if (currentPage > (totalPages - 4)) {
            if (totalPages !== 4) { return (totalPages - 4); }
            else { return 1; }
        }
        else { return (currentPage - 1); }
    };
    const secondValue = () => {
        if (currentPage < 3) { return 2; }
        else if (currentPage > (totalPages - 4)) {
            if (totalPages !== 4) { return (totalPages - 3); }
            else { return 2; }
        }
        else { return (currentPage); }
    };
    const thirdValue = () => {
        if (currentPage < 3) { return 3; }
        else if (currentPage > (totalPages - 4)) {
            if (totalPages !== 4) { return (totalPages - 2); }
            else { return 3; }
        }
        else { return (currentPage + 1); }
    };
    const fourthValue = () => {
        if (currentPage < 3) { return 4; }
        else if (currentPage > (totalPages - 4)) {
            if (totalPages !== 4) { return (totalPages - 1); }
            else { return 4; }
        }
        else { return (currentPage + 2); }
    };
    const fifthValue = () => {
        if (currentPage < 3) { return 5; }
        else if (currentPage > (totalPages - 4)) { return totalPages; }
        else { return (currentPage + 3); }
    };

    useEffect(() => {
        if (data !== defaultData) {
            setData([...defaultData]);
            if (pageSizeProp !== undefined && !totalPagesNumber && autoResetPageIndex) {
                setClientPageIndex(0);
            }
        }
    }, [defaultData, pageSizeProp, totalPagesNumber, autoResetPageIndex]);


    return (
        <div className="table-responsive">
            <Table className={`styled-table ${className}`}>
                <thead>
                    {table.getHeaderGroups().map((groupHeader) => {
                        return (
                            <tr key={groupHeader.id}>
                                {groupHeader.headers.map((header) => {
                                    return (
                                        <th key={`table_${header.id}`} onClick={header.column.getToggleSortingHandler()}>
                                            <div className={`table_${header.index} header-div`}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                {{
                                                    asc: <SortAscendingIcon className="header-icon" />,
                                                    desc: <SortDescendingIcon className="header-icon" />,
                                                }[header.column.getIsSorted() as string] ?? null}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </thead>
                <tbody key={"t-body-styled-table"}>
                    {isLoading &&
                        <tr>
                            <td colSpan={12} style={{ textAlign: "center", padding: "0.5rem" }}>
                                <Spinner animation="border" />
                            </td>
                        </tr>
                    }
                    {table.getRowModel().rows.length ?
                        table.getRowModel().rows.map((row, index) => {
                            return (
                                <React.Fragment key={index}>
                                    <tr className="viewers-row" key={row.id} onClick={onClickRow && onClickRow(row)} >
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <td key={`table_${cell.id}`} data-testid={`table_${cell.column.id}_${index}`}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {row.getIsExpanded() && (
                                        <tr key={`expand-${index}`}>
                                            <td colSpan={row.getVisibleCells().length}>
                                                {onIsExpanded && onIsExpanded(row)}
                                            </td>
                                        </tr>
                                    )
                                    }
                                </React.Fragment>
                            );
                        }) :
                        <tr>
                            <td colSpan={12} style={{ textAlign: "center", padding: "0.5rem" }}>
                                {noDataMessage ?? "No data was found"}
                            </td>
                        </tr>
                    }
                </tbody>
            </Table>
            {(data.length > 10 || (totalPagesNumber && totalPagesNumber > 1)) &&
                <div className="paginator">
                    {
                        showRowsPerPage &&
                        (
                            <div className="rows-page">
                                Rows per page:
                                <select
                                    value={effectivePageSize}
                                    onChange={e => {
                                        table.setPageSize(Number(e.target.value));
                                        setPageSize && setPageSize(Number(e.target.value));
                                    }}
                                >
                                    {Defaults.tablePageSizeOpts().map(pageSize => (
                                        <option key={pageSize} value={pageSize}>
                                            {pageSize}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )
                    }

                    <div className="pages-nav">
                        <button
                            className={
                                currentPageNumber ?
                                    (currentPageNumber === 0 ? "pageIndex pageChangerDisabled" : "pageIndex")
                                    : (!table.getCanPreviousPage() ? "pageIndex pageChangerDisabled" : "pageIndex")
                            }
                            onClick={(e) => {
                                e.preventDefault();
                                table.setPageIndex(0);
                                setCurrentNumber && setCurrentNumber(0);
                            }}
                            disabled={
                                currentPageNumber ?
                                    currentPageNumber === 0 :
                                    !table.getCanPreviousPage()
                            }
                        >
                            {"<<"}
                        </button>
                        <button
                            className={
                                currentPageNumber ?
                                    (currentPageNumber === 0 ? "pageIndex pageChangerDisabled" : "pageIndex")
                                    : (!table.getCanPreviousPage() ? "pageIndex pageChangerDisabled" : "pageIndex")
                            }
                            onClick={(e) => {
                                e.preventDefault();
                                table.previousPage();
                                setCurrentNumber && setCurrentNumber(currentPage - 1);

                            }}
                            disabled={
                                currentPageNumber ?
                                    currentPageNumber === 0 :
                                    !table.getCanPreviousPage()
                            }
                        >
                            {"<"}
                        </button>
                        <div
                            className={currentPage === 0 ? "pageIndexCurrent" : "pageIndex"}
                            onClick={(e) => {
                                e.preventDefault();
                                table.setPageIndex((firstValue()! - 1));
                                setCurrentNumber && setCurrentNumber((firstValue()! - 1));
                            }}
                        >
                            {firstValue()}
                        </div>
                        {totalPages > 1 &&
                            <div
                                className={currentPage === 1 ? "pageIndexCurrent" : "pageIndex"}
                                onClick={(e) => {
                                    e.preventDefault();
                                    table.setPageIndex((secondValue()! - 1));
                                    setCurrentNumber && setCurrentNumber((secondValue()! - 1));
                                }}
                            >
                                {secondValue()}
                            </div>
                        }
                        {totalPages > 2 &&
                            <div
                                className={(
                                    (currentPage === 0) ||
                                    (currentPage === 1) ||
                                    (currentPage === (totalPages - 2) && totalPages !== 4) ||
                                    (currentPage === (totalPages - 1) && totalPages !== 3)
                                ) ?
                                    "pageIndex" : "pageIndexCurrent"}
                                onClick={(e) => {
                                    e.preventDefault();
                                    table.setPageIndex((thirdValue()! - 1));
                                    setCurrentNumber && setCurrentNumber((thirdValue()! - 1));
                                }}
                            >
                                {thirdValue()}
                            </div>
                        }
                        {totalPages > 3 &&
                            <div
                                className={(
                                    (currentPage === (totalPages - 2) && totalPages !== 4) || (currentPage === (totalPages - 1) && totalPages === 4)) ?
                                    "pageIndexCurrent" : "pageIndex"}
                                onClick={(e) => {
                                    e.preventDefault();
                                    table.setPageIndex((fourthValue()! - 1));
                                    setCurrentNumber && setCurrentNumber((fourthValue()! - 1));
                                }}

                            >
                                {fourthValue()}
                            </div>
                        }
                        {totalPages > 4 &&
                            <div
                                className={currentPage === (totalPages - 1) ? "pageIndexCurrent" : "pageIndex"}
                                onClick={(e) => {
                                    e.preventDefault();
                                    table.setPageIndex((fifthValue()! - 1));
                                    setCurrentNumber && setCurrentNumber((fifthValue()! - 1));
                                }}
                            >
                                {fifthValue()}
                            </div>
                        }
                        <button
                            className={
                                currentPageNumber !== undefined ?
                                    (currentPage > (totalPages - 2) ? "pageIndex pageChangerDisabled" : "pageIndex") :
                                    !table.getCanNextPage() ? "pageIndex pageChangerDisabled" : "pageIndex"
                            }
                            onClick={(e) => {
                                e.preventDefault();
                                table.nextPage();
                                setCurrentNumber && setCurrentNumber(currentPage + 1);
                            }}
                            disabled={
                                currentPageNumber !== undefined ?
                                    currentPage > (totalPages - 2) :
                                    !table.getCanNextPage()
                            }
                        >
                            {">"}
                        </button>
                        <button
                            className={
                                currentPageNumber !== undefined ?
                                    (currentPage > (totalPages - 2) ? "pageIndex pageChangerDisabled" : "pageIndex")
                                    : (!table.getCanNextPage() ? "pageIndex pageChangerDisabled" : "pageIndex")
                            }
                            onClick={(e) => {
                                e.preventDefault();
                                table.setPageIndex(totalPages - 1);
                                setCurrentNumber && setCurrentNumber(totalPages - 1);
                            }}
                            disabled={
                                currentPageNumber !== undefined ?
                                    currentPage > (totalPages - 2) :
                                    !table.getCanNextPage()
                            }
                        >
                            {">>"}
                        </button>
                        {totalPages > 5 &&
                            <span className="flex items-center gap-1">
                                <input
                                    min="1"
                                    max={totalPages}
                                    defaultValue={currentPage + 1}
                                    onChange={e => {
                                        const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                        table.setPageIndex(page);
                                        setCurrentNumber && setCurrentNumber(page);
                                    }}
                                    className="border p-1 rounded w-16"
                                />
                                of {totalPages} pages
                            </span>
                        }
                    </div>
                </div>
            }
        </div>
    );
};
